import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { CachedQueryKeys } from "./cachedQueryKeys";
import { fetchDtcSiteProduct, getBrandclubProduct } from "../rest/product";
import { useBCRouting } from "@/providers/BCRoutingContextProvider";
import { useCurrentStacklineSku } from "@/hooks/useCurrentStacklineSku";
import getStacklineSkus from "@/utils/bcProductMetadataUtils";
import { useMemo } from "react";
import { BCQueryOptions } from "./types";
import { ProductRewardSearchResult } from "@brandclub/common-ui";
import { DtcSiteProduct } from "../types";

export const brandclubProductQueryOptions = (
  stacklineSku: string | undefined
) => {
  return queryOptions({
    queryKey: [CachedQueryKeys.DTCProduct, stacklineSku],
    queryFn: async ({ signal }) => {
      if (!stacklineSku) {
        throw new Error("Failed to fetch DTC product: missing stacklineSku");
      }
      return getBrandclubProduct(stacklineSku, signal);
    },
    enabled: !!stacklineSku,
  });
};

export const dtcSiteProductQueryOptions = (productUrl: string) => {
  return queryOptions({
    queryKey: [CachedQueryKeys.SiteProduct, productUrl],
    queryFn: async ({ signal }) => {
      return fetchDtcSiteProduct(productUrl, signal);
    },
  });
};

export const useCurrentBrandclubProduct = <TSelect = ProductRewardSearchResult>(
  args?: BCQueryOptions<
    typeof brandclubProductQueryOptions,
    ProductRewardSearchResult,
    TSelect
  >
) => {
  const stacklineSku = useCurrentStacklineSku();
  const variantSkus = useMemo(
    () => getStacklineSkus().filter((sku) => sku !== stacklineSku),
    [stacklineSku]
  );
  // prefetch all variants of the current product
  useQueries({
    queries: variantSkus.map((sku) => ({
      ...brandclubProductQueryOptions(sku),
      notifyOnChangeProps: [],
    })),
  });

  return useQuery({
    ...brandclubProductQueryOptions(stacklineSku),
    ...args,
    select: args?.select,
  });
};

export const useDtcSiteProduct = <TSelect = DtcSiteProduct>(
  args?: BCQueryOptions<
    typeof dtcSiteProductQueryOptions,
    DtcSiteProduct,
    TSelect
  >
) => {
  const bcRouting = useBCRouting();
  const productUrl =
    bcRouting.location.origin +
    bcRouting.location.pathname.substring(
      bcRouting.location.pathname.indexOf("/products/")
    );
  return useQuery({
    ...dtcSiteProductQueryOptions(productUrl),
    ...args,
    select: args?.select,
  });
};
